import { dashboardRouteSearchSchema } from '../../dashboard/dashboardRouteSearch';
import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { defaultLineItemsFilter, type CampaignStatus, type LineItemDeliveryStatus } from './model';
import {
  lineItemsRouteSearchSchema,
  parseLineItemsRouteSearch,
  serializeLineItemsRouteSearch,
  type LineItemsRouteFilters,
} from './lineItemsRouteSearch';

export function useLineItemsRouteFilters(campaignId: string) {
  const scope = useAgencyCampaignContext();
  const navigate = useNavigate({
    from: scope ? '/a/$agencyId/campaigns/$section' : '/campaigns/campaigns/overview/$campaignId',
  });
  const search = useSearch({ strict: false });
  const filters = useMemo(() => parseLineItemsRouteSearch(search), [search]);
  const update = useCallback(
    (updater: (current: LineItemsRouteFilters) => LineItemsRouteFilters) => {
      void navigate({
        params: true,
        search: (current) => ({
          ...dashboardRouteSearchSchema(current),
          ...(scope ? { campaign: campaignId } : {}),
          ...serializeLineItemsRouteSearch(
            updater(parseLineItemsRouteSearch(lineItemsRouteSearchSchema(current))),
          ),
        }),
        replace: true,
      });
    },
    [navigate, scope, campaignId],
  );
  const setSearch = useCallback(
    (name: string) => {
      update((current) => ({ ...current, name, page: 0 }));
    },
    [update],
  );
  return {
    setAgencies: (agencyIds: string[]) => {
      update((current) => ({ ...current, agencyIds, page: 0 }));
    },
    filters,
    queryFilter: { ...defaultLineItemsFilter, ...filters, campaignId },
    setSearch,
    setStatuses: (status: CampaignStatus[]) => {
      update((current) => ({ ...current, status, page: 0 }));
    },
    setDeliveryStatuses: (deliveryStatus: LineItemDeliveryStatus[]) => {
      update((current) => ({ ...current, deliveryStatus, page: 0 }));
    },
    setPagination: (page: number, size: number) => {
      update((current) => ({ ...current, page, size }));
    },
  };
}
